import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { prof_routes } from "../../../router/all_routes";
import "bootstrap/dist/css/bootstrap.min.css";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { modifyPassword } from "../../../../api/users";
import Swal from "sweetalert2";
import Avvvatars from "avvvatars-react";

// pour restreaindre l'accès à ce composant
import requireAuthentification from "../../../../core/common/autorisations/requireAuthentification";
import { AxiosError } from "axios";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { AppDispatch } from "../../../../core/data/redux/store";
import { useAcademicYear } from "../../../../core/common/context/AcademicYearContext";
import {
  getTeacherById,
  getTeacherHourlyRateByYear,
} from "../../../../api/teachers";
import { getAvatarFileById } from "../../../../api/avatar";

type PasswordField =
  | "oldPassword"
  | "newPassword"
  | "confirmPassword"
  | "currentPassword";

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
    newPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
    confirmPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: " Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: "Le nouveau mot de passe doit être différent de l'ancien",
    path: ["newPassword"],
  });

type changePasswordFormData = z.infer<typeof changePasswordSchema>;

const TeacherProfile = () => {
  const route = prof_routes;
  const dispatch = useDispatch<AppDispatch>();
  const [teacherData, setTeacherData] = useState<any>();
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const { selectedYear } = useAcademicYear();

  // id de l'utilisateur connecté
  const id = useSelector((state: any) => state?.auth?.user?.id);

  const [refresh, setRefresh] = useState(0);
  const [avatar, setAvatar] = useState<any>(null);

  const [passwordVisibility, setPasswordVisibility] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
    currentPassword: false,
  });
  const [user, setUser] = useState({
    firstname: "",
    lastname: "",
    email: "",
    number: "",
    adresse: "",
    birthday: "",
    speciality: "",
  });

  // vérifier si un objet est vide
  function isEmpty(obj: any) {
    return Object.keys(obj).length === 0;
  }

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  // utiliser react-hook-form pour chaque formulaire

  const userReceived = useSelector((state: any) => state.auth.user);

  useEffect(() => {
    // récupérer les données de l'utilisateur depuis le store redux
    setUser(userReceived);
  }, [dispatch, refresh]);

  // 3. Formulaire changement de mot de passe
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: errorsPassword },
    reset: resetPassword,
    clearErrors: clearErrorsPassword,
  } = useForm<changePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  // Fonctions de formattage en FCFA
  const formatFCFA = (value: number) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(value);

  const onSubmitPassword = async (data: changePasswordFormData) => {
    try {
      // console.log("Data envoyé avec succès: ", data);
      const { currentPassword, newPassword } = data;
      const response = await modifyPassword({
        oldPassword: currentPassword,
        newPassword,
      });
      if (!response) {
        Swal.fire({
          title: "Erreur",
          text: "La modification du mot de passe a échoué.",
          icon: "error",
        });
        throw new AxiosError("La modification du mot de passe a échoué.");
      }
      Swal.fire({
        title: "Succès",
        text: "Votre mot de passe a été changé avec succès.",
        icon: "success",
      });
      // masquer le modal
      document.getElementById("closeBtn-passwordModal")?.click();
      resetPassword();
      // clearErrorsPassword();
    } catch (error: any) {
      // console.log(error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      // setErrorPassword("root", {
      //   message:
      //     error instanceof Error ? error.message : "une erreur c'est produite",
      // });
    }
  };

  // on récupère les infos de l'étudiant
  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        if (!id) throw new Error("L'id n'as pas pu être récupéré dans l'URL");
        const teacher = await getTeacherById(id);
        const teacherHoraire = await getTeacherHourlyRateByYear(
          id,
          selectedYear?.id,
        );
        // console.log("Teacher horaire: ", teacherHoraire.data);

        if (selectedYear) {
          const formattedData = selectedYear
            ? {
                ...teacher.data,
                horaire: teacherHoraire.data,
                academicYear: selectedYear,
              }
            : { ...teacher.data };
          setTeacherData(formattedData);
        } else {
          const formattedData = { ...teacher.data };
          setTeacherData(formattedData);
        }
        const avatar = await getAvatarFileById("TeacherProfil", id);
        setAvatar(avatar.data);

        setIsLoading(false); //Les données ne sont plus en chargement
        setHasLoaded(true); //Les données ont été chargé avec succès
      } catch (error: any) {
        // console.log(error);
        // Si erreur (Toast Error)
        toast.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Une erreur s'est produit, Réessayer plus tard",
        );
      }
    };

    fetchTeacherData();
  }, [selectedYear]);

  return (
    <div>
      <>
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            <div className="d-md-flex d-block align-items-center justify-content-between border-bottom pb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Profile</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={route.teacherDashboard}>Accueil</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Mon Profil
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            {errorsPassword.root && (
              <div className="alert alert-danger">
                {errorsPassword.root.message}
              </div>
            )}
            <div className=" d-block mt-3">
              <div className="settings-right-sidebar me-md-3 border-0">
                <div className="card">
                  <div className="card-header">
                    <h5>Information Personnel</h5>
                  </div>
                  <div className="card-body ">
                    <div className="settings-profile-upload">
                      <div className="avatar-wrapper">
                        {avatar?.temporaryUrl ? (
                          <img
                            src={avatar?.temporaryUrl}
                            alt={`${user?.lastname} ${user?.firstname}`}
                            className="avatar-image"
                          />
                        ) : (
                          <div className="">
                            <Avvvatars
                              value={`${user.firstname?.charAt(0) || ""}${
                                user.lastname?.charAt(0) || ""
                              }`}
                              style="character"
                              size={60}
                            />
                          </div>
                        )}
                      </div>
                      <div className="title-upload">
                        <h5>
                          {user?.firstname} {user?.lastname}
                        </h5>
                        <p className="me-2">
                          {user?.speciality &&
                            `Spécialité: ${user?.speciality}`}
                        </p>
                      </div>
                    </div>

                    <dl className="row mb-0">
                      <dt className="col-4 fw-medium text-dark mb-3">Année</dt>
                      <dd className="col-8 mb-3">
                        {selectedYear?.label || "Aucun"}
                      </dd>
                      <dt className="col-4 fw-medium text-dark mb-3">
                        Naissance
                      </dt>
                      <dd className="col-8 mb-3">
                        {new Date(user?.birthday).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </dd>
                      <dt className="col-4 fw-medium text-dark mb-3">Email</dt>
                      <dd className="col-8 mb-3">{user?.email || "Aucun"}</dd>
                      <dt className="col-4 fw-medium text-dark mb-3">
                        Téléphone
                      </dt>
                      <dd className="col-8 mb-3">{user?.number || "Aucun"}</dd>
                      <dt className="col-4 fw-medium text-dark mb-3">
                        Adresse
                      </dt>
                      <dd className="col-8 mb-3">
                        {user?.adresse || "Aucune"}
                      </dd>
                      <dt className="col-4 fw-medium text-dark mb-3">
                        Taux Horaire
                      </dt>
                      <dd className="col-8 mb-3">
                        {teacherData?.horaire.length > 0
                          ? formatFCFA(teacherData?.horaire[0].amount)
                          : "Non défini"}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
              <div className="flex-fill ps-0 border-0">
                <form>
                  <div className="d-md-flex">
                    <div className="flex-fill">
                      <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5>Mot de passe</h5>
                          <Link
                            to="#"
                            className="btn btn-primary btn-sm"
                            data-bs-toggle="modal"
                            data-bs-target="#change_password"
                            onClick={() => {
                              if (!isEmpty(errorsPassword))
                                clearErrorsPassword();
                            }}
                          >
                            Changer
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {/* /Page Wrapper */}

        {/* Change Password */}
        <div className="modal fade" id="change_password">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Changer le mot de passe</h4>
                <button
                  id="closeBtn-passwordModal"
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form
                className="needs-validation"
                onSubmit={handleSubmitPassword(onSubmitPassword)}
              >
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Mot de passe actuel
                        </label>
                        <div className="pass-group d-flex">
                          <input
                            id="edit_currentPassword_password"
                            {...registerPassword("currentPassword")}
                            type={
                              passwordVisibility.oldPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                          />
                          <span
                            className={`ti toggle-passwords ${
                              passwordVisibility.oldPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("oldPassword")
                            }
                          ></span>
                        </div>
                      </div>
                      {errorsPassword.currentPassword && (
                        <div className="text-danger">
                          {errorsPassword.currentPassword.message}
                        </div>
                      )}
                      <div className="mb-3">
                        <label className="form-label">
                          Nouveau mot de passe
                        </label>
                        <div className="pass-group d-flex">
                          <input
                            id="edit_newPassword_password"
                            {...registerPassword("newPassword")}
                            type={
                              passwordVisibility.newPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                          />
                          <span
                            className={`ti toggle-passwords ${
                              passwordVisibility.newPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("newPassword")
                            }
                          ></span>
                        </div>
                      </div>
                      {errorsPassword.newPassword && (
                        <div className="text-danger">
                          {errorsPassword.newPassword.message}
                        </div>
                      )}
                      <div className="mb-0">
                        <label className="form-label">
                          Confirmer le mot de passe
                        </label>
                        <div className="pass-group d-flex">
                          <input
                            id="edit_confirmPassword_password"
                            {...registerPassword("confirmPassword")}
                            type={
                              passwordVisibility.confirmPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                          />
                          <span
                            className={`ti toggle-passwords ${
                              passwordVisibility.confirmPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("confirmPassword")
                            }
                          ></span>
                        </div>
                      </div>
                      {errorsPassword.confirmPassword && (
                        <div className="text-danger">
                          {errorsPassword.confirmPassword.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Annuler
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    // data-bs-dismiss="modal"
                  >
                    Enregistrer les modifications
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Change Password */}
      </>
    </div>
  );
};

export default requireAuthentification(TeacherProfile);
